import React, { useEffect, useRef, useState } from "react";
import { StyledSearchListForm } from "./style";
import { DropDownListOptionType } from "types/common/CommonType";
import { Util } from "utils";
import { Tooltip } from "components/tooltip/Tooltip";

/**
 * 드롭다운 리스트 폼 컴포넌트 속성
 */
interface SearchListFormProps {
  size: "large" | "medium" | "small";
  option: DropDownListOptionType;
  isFocused?: boolean;
  showIndent?: boolean;
  searchValue?: string;
  enableFontSelection?: boolean;
  handleValue?: (value: DropDownListOptionType) => void;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
}

/**
 * SearchListForm component
 * @author hc_yang
 * @param {SearchListFormProps} param
 * @returns JSX.Element
 */
export const SearchListForm = ({
  size,
  option,
  isFocused = false,
  showIndent = false,
  searchValue,
  enableFontSelection = false,
  handleValue,
  onClick,
}: SearchListFormProps) => {
  const contentRef = useRef<HTMLParagraphElement | null>(null);
  const parentRef = useRef<HTMLDivElement | null>(null);
  const [label, setLabel] = useState<string>(option.label);
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (onClick) onClick(e);
    handleValue && handleValue(option);
  };

  const checkContentOverflow = () => {
    if (contentRef.current) {
      return contentRef.current.scrollWidth > contentRef.current.clientWidth;
    }
  };

  const handleMouseEnter = () => {
    if (checkContentOverflow()) setIsHovered(true);
  };

  const handleMouseLeave = () => {
    if (isHovered) setIsHovered(false);
  };

  useEffect(() => {
    if (searchValue) {
      const regex = new RegExp(Util.escapeRegExp(searchValue), "gi");
      setLabel(
        option.label.replace(
          regex,
          `<span style="background-color: var(--sec-orange-3);">$&</span>`
        )
      );
    } else {
      setLabel(option.label);
    }
  }, [searchValue]);

  return (
    <StyledSearchListForm
      ref={parentRef}
      $size={size}
      $focused={isFocused}
      $disabled={option.disabled || false}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {showIndent && <span className="indent-box" />}
      <p
        ref={contentRef}
        className="label"
        dangerouslySetInnerHTML={{ __html: label }}
        style={
          (enableFontSelection && {
            fontFamily: option.value as string,
            fontWeight: 400,
          }) ||
          undefined
        }
      />
      {isHovered && (
        <Tooltip
          direction="top"
          label={option.label}
          parentRef={parentRef}
          wordWrap={false}
        />
      )}
    </StyledSearchListForm>
  );
};
